<template>
    <TailwindLayout title="Review Dispute">
        <div class="space-y-6">
            <BridgeBackLink />

            <div v-if="$page.props.flash && $page.props.flash.success" class="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
                {{ $page.props.flash.success }}
            </div>
            <div v-if="$page.props.flash && $page.props.flash.error" class="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                {{ $page.props.flash.error }}
            </div>

            <header class="flex items-start justify-between gap-4">
                <div>
                    <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">
                        Dispute <span class="font-mono text-base">{{ dispute.stripe_dispute_id }}</span>
                    </h1>
                    <p class="mt-1 text-sm text-gray-600">
                        Charge <span class="font-mono">{{ dispute.stripe_charge_id }}</span>
                    </p>
                </div>
                <a :href="stripe_url" target="_blank" rel="noopener" class="primaryButton">Open in Stripe</a>
            </header>

            <!-- Dispute -->
            <section class="bg-white shadow sm:rounded-lg p-6">
                <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <dt class="text-gray-500">Status</dt>
                    <dd>{{ dispute.status || '—' }}</dd>
                    <dt class="text-gray-500">Respond by</dt>
                    <dd class="font-semibold">{{ dispute.respond_by || 'Not provided by Stripe' }}</dd>
                    <dt class="text-gray-500">Reason</dt>
                    <dd>{{ dispute.reason || '—' }}</dd>
                    <dt class="text-gray-500">Amount</dt>
                    <dd>{{ dispute.amount ? '$' + dispute.amount + ' ' + dispute.currency : '—' }}</dd>
                    <dt class="text-gray-500">Charge</dt>
                    <dd>{{ dispute.charge_description || '—' }}</dd>
                    <dt class="text-gray-500">Evidence from</dt>
                    <dd>{{ evidenceKindLabel }}</dd>
                    <dt class="text-gray-500">Radar risk at payment</dt>
                    <dd data-test="radar-risk">
                        {{ radarRiskLabel }}
                        <span class="block text-xs text-gray-500">Stripe's fraud risk rating when the payment was made. Not the chance of winning; Stripe shows that only in its dashboard.</span>
                    </dd>
                    <dt class="text-gray-500">Evidence prepared</dt>
                    <dd>{{ dispute.evidence_staged_at || 'Not yet' }}</dd>
                    <dt class="text-gray-500">Submitted to the bank</dt>
                    <dd>
                        <span v-if="dispute.evidence_submitted_at">{{ dispute.evidence_submitted_at }} by {{ dispute.evidence_submitted_by || 'unknown' }}</span>
                        <span v-else>No</span>
                    </dd>
                </dl>
                <p v-if="dispute.evidence_stage_status === 'failed'" class="mt-4 text-sm text-red-700">
                    Last prepare failed: {{ dispute.evidence_stage_error }}
                </p>
            </section>

            <!-- No evidence -->
            <section v-if="!has_evidence_source" class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <p class="text-sm text-yellow-800">
                    <strong>No evidence; answer in Stripe.</strong> RentRisk found no application and no RentRisk account
                    behind this charge, so it has nothing to prepare. Respond to the dispute in the Stripe dashboard.
                </p>
            </section>

            <template v-else>
                <!-- Prepare -->
                <section v-if="can_prepare" class="bg-white shadow sm:rounded-lg p-6">
                    <h3 class="text-lg font-semibold mb-2">Prepare evidence</h3>
                    <p class="text-sm text-gray-600 mb-4">
                        Uploads the evidence PDF and saves the evidence on the dispute in Stripe as a draft.
                        Nothing is sent to the bank.
                    </p>
                    <button type="button" class="primaryButton" :disabled="preparing" @click="prepare">
                        {{ preparing ? 'Preparing…' : 'Prepare evidence' }}
                    </button>
                </section>

                <!-- What will be sent -->
                <section v-if="submission_fields" class="bg-white shadow sm:rounded-lg p-6">
                    <div class="flex items-start justify-between mb-3">
                        <h3 class="text-lg font-semibold">Evidence that will be sent</h3>
                        <div class="flex gap-4 text-sm">
                            <a :href="evidence_pdf_url" class="text-rent-risk-blue-400 hover:underline">Evidence PDF</a>
                            <a :href="evidence_page_url" class="text-rent-risk-blue-400 hover:underline">Full evidence page</a>
                        </div>
                    </div>
                    <p class="text-xs text-gray-500 mb-4">These are exactly the fields Submit sends to Stripe. <span class="font-mono">uncategorized_file</span> is the uploaded evidence PDF.</p>
                    <dl class="divide-y divide-gray-100 text-sm">
                        <div v-for="(value, field) in submission_fields" :key="field" class="grid grid-cols-3 gap-4 py-2">
                            <dt class="font-mono text-gray-500">{{ field }}</dt>
                            <dd class="col-span-2 whitespace-pre-wrap break-words">{{ value }}</dd>
                        </div>
                    </dl>
                </section>

                <!-- Submit -->
                <section v-if="submission_fields && !submit_blocker" class="bg-white shadow sm:rounded-lg p-6 border border-red-200">
                    <h3 class="text-lg font-semibold mb-2">Submit to bank</h3>
                    <p class="text-sm text-gray-600 mb-4">
                        Sends the evidence above to the bank. This is final: Stripe does not accept changes after submission.
                    </p>
                    <label class="flex items-center gap-2 text-sm mb-4">
                        <input v-model="reviewed" type="checkbox" class="rounded border-gray-300" />
                        I reviewed this evidence
                    </label>
                    <p v-if="$page.props.errors && $page.props.errors.reviewed" class="text-sm text-red-700 mb-4">{{ $page.props.errors.reviewed }}</p>
                    <button type="button" class="primaryButton" :disabled="!reviewed || submitting" @click="submit">
                        {{ submitting ? 'Submitting…' : 'Submit to bank' }}
                    </button>
                </section>
                <p v-else-if="submission_fields && submit_blocker" class="text-sm text-gray-600">
                    {{ submit_blocker }}
                </p>
            </template>
        </div>
    </TailwindLayout>
</template>

<script>
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import BridgeBackLink from '@/Shared/BridgeBackLink.vue';
import { router } from '@inertiajs/vue3';

export default {
    components: { TailwindLayout, BridgeBackLink },
    props: {
        dispute: { type: Object, required: true },
        has_evidence_source: { type: Boolean, required: true },
        can_prepare: { type: Boolean, required: true },
        submission_fields: { type: Object, default: null },
        submit_blocker: { type: String, default: null },
        stripe_url: { type: String, required: true },
        evidence_pdf_url: { type: String, default: null },
        evidence_page_url: { type: String, default: null },
        evidence_kind: { type: String, default: null },
    },
    computed: {
        evidenceKindLabel() {
            return {
                application: 'The rental application behind the charge',
                account: 'The landlord account behind the charge (subscription or other account charge)',
            }[this.evidence_kind] || 'Not found yet';
        },
        radarRiskLabel() {
            const { radar_risk_level: level, radar_risk_score: score } = this.dispute;
            if (!level && score === null) {
                return 'Not read yet; Sync from Stripe or Prepare reads it';
            }
            return [level, score !== null ? `score ${score} of 100` : null].filter(Boolean).join(', ');
        },
    },
    data() {
        return {
            preparing: false,
            submitting: false,
            reviewed: false,
        };
    },
    methods: {
        prepare() {
            this.preparing = true;
            router.post(route('bridge.disputes.prepare', this.dispute.stripe_dispute_id), {}, {
                preserveScroll: true,
                onFinish: () => { this.preparing = false; },
            });
        },
        submit() {
            if (!window.confirm(`Submit this evidence to the bank for dispute ${this.dispute.stripe_dispute_id}? This cannot be undone.`)) {
                return;
            }
            this.submitting = true;
            router.post(route('bridge.disputes.submit', this.dispute.stripe_dispute_id), { reviewed: this.reviewed }, {
                preserveScroll: true,
                onFinish: () => { this.submitting = false; },
            });
        },
    },
};
</script>
